/* eslint-disable camelcase */
import has from 'lodash/has.js';
import isEmpty from 'lodash/isEmpty.js';

import axios from '@/plugins/axios.js';
import { DialogService } from '@/services/DialogService.js';
import toastService from '@/services/ToastService.js';

export default class QuizAttemptHelper {
    constructor({ store = null, router = null, payload = null, attemptId = null }) {
        this.store = store;
        this.router = router;
        this.user_id = store.rootGetters['User/get']('id');
        this.quiz_active_id = attemptId || store.rootGetters['User/get']('quiz_active_id');
        this.payload = payload;
        this.attemptId = attemptId;
    }

    async authorizeQuizAttempt({ quizType = null }) {
        const type = this.userHasActiveQuiz() || this.attemptId ? 'get' : 'post';

        const data = await this.launchQuiz({ method: type, quizType });

        if (!this.hasCriticalUserData()) {
            const isLockdown =
                this.payload.lockdown ||
                data?.quiz_activity?.lockdown ||
                data?.survey_activity?.lockdown ||
                data?.automated_survey_assignment?.is_locked_now;

            if (isLockdown) {
                this.store.commit('User/SET', { field: 'quiz_active_id', value: data.id }, { root: true });
            }

            if (this.payload.assignment) {
                this.store.commit(
                    'User/SET',
                    {
                        field: 'quiz_activity',
                        value: this.payload.assignment,
                    },
                    { root: true }
                );
            } else {
                this.store.commit(
                    'User/SET',
                    {
                        field: 'quiz_activity',
                        value: data.activity || data.quiz_activity?.activity || data.survey_activity?.activity,
                    },
                    { root: true }
                );
            }

            this.store.commit('User/SET', { field: 'quiz_id', value: data.quiz_version_id }, { root: true });
        }

        return data;
    }

    handleError(error) {
        const errorMessage = error?.response?.data?.message;

        return DialogService.alert({
            title: 'Error',
            message: `
                     <p>Looks like something went wrong</p>
                     </br>
                     <p>${!errorMessage ? error : `Error: ${errorMessage}`}</p>
                    `,
            type: 'is-danger',
            hasIcon: true,
            icon: 'exclamation-circle',
            iconPack: 'unicons',
            ariaRole: 'alertdialog',
            ariaModal: true,
            onConfirm: () => {
                if (error?.response?.data?.code === 400) return;

                // Hard redirect to activity page
                toastService.open('Reloading page');

                const { courseId, activityId } = this.payload;

                window.location.href = `/courses/${courseId}/activities/${activityId}`;
            },
        });
    }

    async launchQuiz({ method, quizType = null }) {
        try {
            let attemptKey = null;
            let api = `${this.store.getters.api('quiz_attempt')}${method === 'get' ? `/${this.quiz_active_id}` : ''}`;

            if (quizType === 'survey') {
                attemptKey = 'survey_activity_id';
            } else if (quizType !== 'automatedSurvey') {
                attemptKey = 'quiz_activity_id';
            }

            if (method === 'get') {
                if (quizType === 'survey') {
                    api += '?include=surveyActivity.activity';
                } else {
                    api += '?include=quizActivity.activity';
                }
            }

            const payload = {
                quiz_version_id: this.payload.quizVersionId,
                user_id: this.user_id,
                password: this.payload.password,
                is_preview: this.payload.preview,
            };

            if (quizType !== 'automatedSurvey') {
                payload[attemptKey] = this.userHasActiveQuiz() ? this.quiz_active_id : this.payload.quizActivityId;
            }

            return await axios[method](`${api}`, payload, { errorHandle: false }).then(resp => resp.data.data);
        } catch (e) {
            this.handleError(e);

            return e;
        }
    }

    navigateToQuiz(type, quizId, attemptId) {
        this.router.push({
            name: `${type}-attempt`,
            params: {
                ...this.payload,
                attemptId,
                id: quizId,
            },
        });
    }

    // eslint-disable-next-line class-methods-use-this
    parseSelectedAnswers(item) {
        return item.map(question => {
            const multipleAnswer = question.type.includes('answer');
            const hasAnswerObject = has(question.question, 'answers');
            const freeForm = question.type === 'free_form';

            question.question.selected_answer = [];
            question.question.finalized_at = null;
            question.question.finalized = false;
            question.question.isChanging = false;

            // If Show Possible Answers List is false, answers object is not present

            if (hasAnswerObject && question.question.answers.some(answer => !answer.display_order)) {
                question.question.answers.forEach((answer, index) => (answer.display_order = index));
            }

            if (freeForm) {
                question.question.answer_body = null;
            }

            if (!isEmpty(question.quiz_attempt_answers)) {
                let selectedAnswer;

                if (!multipleAnswer) {
                    [selectedAnswer] = question.quiz_attempt_answers;

                    if (hasAnswerObject) {
                        const index = question.question.answers.findIndex(
                            answer => Number(answer.id) === Number(selectedAnswer.answer_id)
                        );

                        if (index >= 0) {
                            question.question.answers[index].selected = true;

                            question.question.selected_answer = question.question.answers[index].display_order;
                        }
                    }

                    if (selectedAnswer.finalized_at != null) {
                        question.question.finalized = true;
                        question.question.finalized_at = selectedAnswer.finalized_at;
                    }
                }

                if (multipleAnswer) {
                    selectedAnswer = [];

                    if (hasAnswerObject) {
                        question.quiz_attempt_answers.forEach(answer => {
                            const index = question.question.answers.findIndex(
                                possibleAnswer => Number(possibleAnswer.id) === Number(answer.answer_id)
                            );

                            if (index >= 0) {
                                question.question.answers[index].selected = true;

                                selectedAnswer.push(question.question.answers[index].display_order);

                                question.question.selected_answer = selectedAnswer;
                            }
                        });
                    }

                    if (question.quiz_attempt_answers.some(answer => answer.finalized_at != null)) {
                        const index = question.quiz_attempt_answers.findIndex(answer => answer.finalized_at != null);

                        question.question.finalized = true;
                        question.question.finalized_at = question.quiz_attempt_answers[index].finalized_at;
                    }
                }

                if (freeForm) {
                    if (hasAnswerObject) {
                        question.quiz_attempt_answers.forEach(answer => {
                            question.question.answers[0].answer_body = answer.answer_body;

                            question.question.answers[0].answer_attempt_id = answer.id;

                            question.question.answers[0].finalized = answer.finalized;

                            question.question.answers[0].finalized_at = answer?.finalized_at;
                        });
                    }
                }
            }

            return question;
        });
    }

    userHasActiveQuiz() {
        return this.store.rootGetters['User/hasActiveQuiz'];
    }

    hasCriticalUserData() {
        const required = ['quiz_active_id', 'quiz_activity', 'quiz_id'];

        return required.every(check => this.store.rootGetters['User/get'](check));
    }
}
